import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { realGbp } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ArrowLeft, ExternalLink, Loader2, Pencil, Plus, ShieldCheck, Trash2 } from "lucide-react";
import DealFormDialog, { KINDS } from "@/components/admin/DealFormDialog";
import DemoResults from "@/components/admin/DemoResults";
import SignupsPanel from "@/components/admin/SignupsPanel";

const valueOf = (d) =>
  d.kind === "subscription" ? `${realGbp(d.monthly_price)}/mo` : d.kind === "savings" ? `${d.rate}% AER`
    : `${d.cashback_pct}% cashback${d.monthly_cap ? ` · cap ${realGbp(d.monthly_cap)}/mo` : ""}`;

export default function DealsAdmin({ onBack }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("subscription");
  const [section, setSection] = useState("deals");
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [checked, setChecked] = useState("");

  const load = useCallback(() => api.get("/admin/deals").then(({ data }) => { setData(data); setChecked(data.checked); })
    .catch((err) => toast.error(formatApiError(err.response?.data?.detail))), []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (d, active) => {
    setData((x) => ({ ...x, deals: x.deals.map((y) => (y.id === d.id ? { ...y, active } : y)) }));
    try {
      await api.patch(`/admin/deals/${d.id}/active`, { active });
      toast.success(active ? "Deal turned on" : "Deal turned off");
    } catch { toast.error("Couldn't update deal"); load(); }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/deals/${deleting.id}`);
      toast.success("Deal deleted");
      load();
    } catch { toast.error("Couldn't delete deal"); }
    setDeleting(null);
  };

  const saveChecked = async () => {
    try {
      await api.put("/admin/deals-settings", { checked });
      setData((x) => ({ ...x, checked }));
      toast.success("Date updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const rows = (data?.deals || []).filter((d) => d.kind === tab);

  return (
    <div className="mm-fade-up max-w-5xl" data-testid="deals-admin-page">
      <button data-testid="deals-admin-back-button" onClick={onBack} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-5">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </button>
      <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#00C896] flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Admin</p>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">{{ deals: "Better Deals list", demo: "Demo results", signups: "Sign-ups & waitlist" }[section]}</h1>
      <div className="mt-4 inline-flex rounded-full border border-white/10 bg-[#0D0D0D] p-1">
        {[["deals", "Deals"], ["demo", "Demo results"], ["signups", "Sign-ups"]].map(([id, label]) => (
          <button key={id} data-testid={`admin-section-${id}`} onClick={() => setSection(id)}
            className={`rounded-full px-3 sm:px-4 py-1.5 text-sm transition-colors ${section === id ? "bg-[#00C896] text-[#04140D] font-semibold" : "text-slate-400 hover:text-white"}`}>{label}</button>
        ))}
      </div>

      {section === "signups" ? <div className="mt-6"><SignupsPanel /></div> : section === "demo" ? <div className="mt-6"><DemoResults /></div> : (<>
      <p className="text-slate-400 text-sm mt-4 max-w-xl">Changes go live for every user straight away. Deals that are turned off are never suggested.</p>

      {!data ? (
        <div className="h-48 flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#00C896]" /></div>
      ) : (
        <div className="mt-6 space-y-5">
          <div className="mm-glow-card p-5 flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1">
              <p className="text-sm font-medium">Prices and rates checked</p>
              <p className="text-xs text-slate-500">Shown under Better Deals on every dashboard.</p>
              <Input data-testid="deals-admin-checked-input" value={checked} onChange={(e) => setChecked(e.target.value)} maxLength={40}
                className="mt-2 bg-[#0D0D0D] border-white/10 text-white focus-visible:ring-[#00C896] sm:max-w-xs" />
            </div>
            <Button data-testid="deals-admin-checked-save" onClick={saveChecked} disabled={checked.trim().length < 3 || checked === data.checked}
              className="rounded-full bg-[#00C896] hover:bg-[#00E5AC] text-[#04140D] font-semibold">Save date</Button>
          </div>

          <div className="mm-glow-card p-5 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap gap-2">
                {KINDS.map((k) => (
                  <button key={k.id} data-testid={`deals-admin-tab-${k.id}`} onClick={() => setTab(k.id)}
                    className={`rounded-full px-3 py-1 text-xs border transition-colors ${tab === k.id ? "border-[#00C896]/40 bg-[#00C896]/15 text-[#4DE3BF]" : "border-white/10 text-slate-400 hover:text-white"}`}>
                    {k.label} <span className="text-slate-500">({data.deals.filter((d) => d.kind === k.id).length})</span>
                  </button>
                ))}
              </div>
              <Button data-testid="deals-admin-add-button" onClick={() => { setEditing(null); setFormOpen(true); }} size="sm" className="rounded-full bg-[#00C896] hover:bg-[#00E5AC] text-[#04140D] font-semibold">
                <Plus className="h-4 w-4 mr-1" /> Add deal
              </Button>
            </div>

            <div className="mt-4 divide-y divide-white/6">
              {rows.map((d) => (
                <div key={d.id} data-testid={`deals-admin-row-${d.key}`} className={`py-3.5 flex flex-col sm:flex-row sm:items-center gap-3 ${d.active ? "" : "opacity-50"}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold">{d.name}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {d.kind === "subscription" && <>Replaces {d.service} · matches “{(d.keywords || []).join(", ")}” · </>}{d.provider}
                    </p>
                    <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#4DE3BF] hover:underline inline-flex items-center gap-1 max-w-full truncate">
                      <span className="truncate">{d.url}</span> <ExternalLink className="h-3 w-3 shrink-0" />
                    </a>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4">
                    <span data-testid={`deals-admin-value-${d.key}`} className="font-num text-sm font-semibold text-[#00C896] min-w-[96px]">{valueOf(d)}</span>
                    <Switch data-testid={`deals-admin-toggle-${d.key}`} checked={d.active} onCheckedChange={(v) => toggle(d, v)} className="data-[state=checked]:bg-[#00C896]" />
                    <Button data-testid={`deals-admin-edit-${d.key}`} variant="ghost" size="icon" onClick={() => { setEditing(d); setFormOpen(true); }} className="h-8 w-8 text-slate-400 hover:text-white hover:bg-white/5"><Pencil className="h-4 w-4" /></Button>
                    <Button data-testid={`deals-admin-delete-${d.key}`} variant="ghost" size="icon" onClick={() => setDeleting(d)} className="h-8 w-8 text-slate-400 hover:text-rose-300 hover:bg-rose-500/10"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
              {rows.length === 0 && <p data-testid="deals-admin-empty" className="py-8 text-center text-sm text-slate-500">No deals of this type yet.</p>}
            </div>
          </div>
        </div>
      )}
      </>)}

      <DealFormDialog open={formOpen} onOpenChange={setFormOpen} deal={editing} defaultKind={tab} onSaved={load} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="deals-admin-delete-dialog" className="bg-[#14181D] border-white/10 text-white rounded-2xl w-[calc(100%-2rem)]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this deal?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">“{deleting?.name}” will stop appearing for all users. To pause it instead, turn it off.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deals-admin-delete-cancel" className="bg-transparent border-white/15 text-slate-300 hover:bg-white/5 hover:text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="deals-admin-delete-confirm" onClick={remove} className="bg-rose-500 hover:bg-rose-400 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
